/**
 * Sister Spotlights Page
 * 
 * Quarterly member features highlighting success stories and transformations.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Star, Loader2, Sparkles, Heart, MessageCircle, Send,
  ArrowLeft, Trophy, Users, Crown
} from 'lucide-react';

const API_URL = window.location.origin;

const REACTIONS = ["❤️", "🎉", "🤝🏾", "💡", "🤔", "👏🏾", "💪🏾", "🙌🏾", "🙏🏾", "💎", "🔥", "✨"];

const SisterSpotlightsPage = () => {
  const [loading, setLoading] = useState(true);
  const [featured, setFeatured] = useState(null);
  const [spotlights, setSpotlights] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showNominateDialog, setShowNominateDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('featured');
  
  // Nomination form
  const [nomineeId, setNomineeId] = useState('self');
  const [category, setCategory] = useState('');
  const [story, setStory] = useState('');
  const [whyNominated, setWhyNominated] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [featuredRes, spotlightsRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/spotlights/featured`, { headers }),
        axios.get(`${API_URL}/api/spotlights`, { headers }),
        axios.get(`${API_URL}/api/spotlights/categories`)
      ]);
      
      setFeatured(featuredRes.data.spotlight);
      setSpotlights(spotlightsRes.data.spotlights || []);
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch spotlights:', error);
    } finally {
      setLoading(false);
    }
  };

  const submitNomination = async () => {
    if (!category || !story) {
      toast.error('Please select a category and share your story');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/spotlights/nominate`,
        {
          nominee_id: nomineeId === 'self' ? undefined : nomineeId,
          category,
          story,
          why_nominated: whyNominated
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowNominateDialog(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit nomination');
    } finally {
      setSubmitting(false);
    }
  };

  const reactToSpotlight = async (spotlightId, reaction) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/spotlights/${spotlightId}/react`,
        { reaction },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Celebration sent!');
    } catch (error) {
      toast.error('Failed to react');
    }
  };

  const resetForm = () => {
    setNomineeId('self');
    setCategory('');
    setStory('');
    setWhyNominated('');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-terracotta" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="sister-spotlights-page">
        {/* Header */}
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center text-sm text-gray-600 hover:text-forest mb-4">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </Link>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Star className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h1 className="text-2xl font-heading text-forest">Sister Spotlights</h1>
                <p className="text-gray-600">Celebrating the wins and transformations of our community</p>
              </div>
            </div>
            
            <Button
              onClick={() => setShowNominateDialog(true)}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="nominate-btn"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Share Your Story
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="featured">Featured Spotlight</TabsTrigger>
            <TabsTrigger value="all">All Spotlights</TabsTrigger>
          </TabsList>

          {/* Featured Spotlight */}
          <TabsContent value="featured">
            {featured ? (
              <Card className="card-base bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
                <CardContent className="p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Crown className="h-5 w-5 text-amber-500" />
                    <Badge className="bg-amber-500 text-white">Featured Sister</Badge>
                    <span className="text-sm text-gray-500">{featured.quarter}</span>
                  </div>
                  
                  <div className="flex items-start gap-6">
                    <Avatar className="h-24 w-24 border-4 border-amber-300">
                      <AvatarImage src={featured.nominee_image} />
                      <AvatarFallback className="bg-amber-100 text-amber-600 text-2xl">
                        {featured.nominee_name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1">
                      <h2 className="text-2xl font-heading text-forest mb-1">
                        {featured.nominee_name}
                      </h2>
                      <Badge className="mb-4 bg-amber-100 text-amber-700">
                        {featured.category_label}
                      </Badge>
                      
                      <p className="text-gray-700 leading-relaxed mb-4">
                        "{featured.story}"
                      </p>
                      
                      {featured.why_nominated && (
                        <p className="text-sm text-gray-500 italic">
                          Nominated by {featured.submitter_name}: "{featured.why_nominated}"
                        </p>
                      )}
                      
                      {/* Reactions */}
                      <div className="flex items-center gap-3 mt-6">
                        <span className="text-sm text-gray-500">Celebrate:</span>
                        {REACTIONS.map(reaction => (
                          <button
                            key={reaction}
                            onClick={() => reactToSpotlight(featured.id, reaction)}
                            className="text-2xl hover:scale-125 transition-transform"
                          >
                            {reaction}
                          </button>
                        ))}
                        {featured.reactions?.length > 0 && (
                          <span className="text-sm text-gray-500 ml-2">
                            {featured.reactions.length} celebrations
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="card-base">
                <CardContent className="p-8 text-center">
                  <Star className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-heading text-forest mb-2">No Featured Spotlight Yet</h3>
                  <p className="text-gray-600 mb-4">
                    Be the first to share your transformation story!
                  </p>
                  <Button
                    onClick={() => setShowNominateDialog(true)}
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                  >
                    Share Your Story
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* All Spotlights */}
          <TabsContent value="all">
            {spotlights.length === 0 ? (
              <Card className="card-base">
                <CardContent className="p-8 text-center">
                  <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-heading text-forest mb-2">No Spotlights Yet</h3>
                  <p className="text-gray-600">
                    Share your story or nominate a sister who inspires you!
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {spotlights.map(spotlight => (
                  <Card key={spotlight.id} className={`card-base ${spotlight.status === 'featured' ? 'border-amber-300 bg-amber-50/30' : ''}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <Avatar className="h-14 w-14">
                          <AvatarImage src={spotlight.nominee_image} />
                          <AvatarFallback className="bg-amber-100 text-amber-600">
                            {spotlight.nominee_name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-forest">
                              {spotlight.nominee_name}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {spotlight.category_label}
                            </Badge>
                            {spotlight.status === 'featured' && (
                              <Badge className="bg-amber-500 text-white text-xs">
                                <Crown className="h-3 w-3 mr-1" />
                                Featured
                              </Badge>
                            )}
                            {spotlight.status === 'approved' && (
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                Approved
                              </Badge>
                            )}
                            {spotlight.status === 'pending' && (
                              <Badge className="bg-gray-100 text-gray-600 text-xs">
                                Shared
                              </Badge>
                            )}
                          </div>
                          
                          <p className="text-gray-700 text-sm mb-3">
                            "{spotlight.story}"
                          </p>
                          
                          <div className="flex items-center gap-2">
                            {REACTIONS.slice(0, 4).map(reaction => (
                              <button
                                key={reaction}
                                onClick={() => reactToSpotlight(spotlight.id, reaction)}
                                className="text-lg hover:scale-125 transition-transform"
                              >
                                {reaction}
                              </button>
                            ))}
                            <span className="text-xs text-gray-500">
                              {spotlight.quarter}
                            </span>
                            {spotlight.reactions?.length > 0 && (
                              <span className="text-xs text-gray-500">
                                • {spotlight.reactions.length} celebrations
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Nomination Dialog */}
        <Dialog open={showNominateDialog} onOpenChange={setShowNominateDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-500" />
                Share Your Story
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <p className="text-sm text-gray-600">
                Every sister has a story worth celebrating. Share your transformation, 
                win, or breakthrough - or nominate another sister who inspires you!
              </p>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-forest">Category</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.emoji} {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-forest">Your Story *</label>
                <Textarea
                  placeholder="Share your transformation, win, or breakthrough..."
                  value={story}
                  onChange={(e) => setStory(e.target.value)}
                  rows={5}
                  maxLength={1000}
                />
                <p className="text-xs text-gray-400 text-right">{story.length}/1000</p>
              </div>
              
              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="text-sm text-amber-700">
                  Your story will be immediately visible to all sisters! High-quality stories earn <strong>100 diamonds</strong> and may be 
                  <strong> featured by SHE</strong> to inspire the entire community.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNominateDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={submitNomination}
                disabled={submitting || !category || !story}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" />Submit Story</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default SisterSpotlightsPage;
